import React, { useEffect, useCallback, useContext, useState } from "react";
import { useDropzone as useReactDropzone } from "react-dropzone";

import { handleDropAccepted, handleDropRejected } from "../helpers";

import DropzoneContext from "../context";

export const useDropzone = ({
    uploadFiles,
    initialMaximumFiles,
    minFileSizeBytes = 2621440, // 2.5MB temporary
    maxFileSizeBytes = 52428800, // 50MB temporary
    addedFilesComponent,
    onDropAccepted,
    filesInDropzoneCount
} = {}) => {
    const [maxFiles, setMaxFiles] = useState(initialMaximumFiles);

    useEffect(() => {
        setMaxFiles(initialMaximumFiles - filesInDropzoneCount);
    }, [initialMaximumFiles, filesInDropzoneCount]);

    const {
        isDisabledDropzone,
        setDisabledDropzone,
        hasRejectedFileErrors,
        setRejectedFileErrors
    } = useContext(DropzoneContext);

    const handleClearErrors = useCallback(
        () => !!setRejectedFileErrors && setRejectedFileErrors([]),
        []
    );

    const {
        isDragAccept,
        isDragReject,
        acceptedFiles,
        getRootProps,
        getInputProps,
        open: openFilePicker
    } = useReactDropzone({
        minSize: minFileSizeBytes,
        maxSize: maxFileSizeBytes,
        disabled: isDisabledDropzone,
        maxFiles: maxFiles,
        useFsAccessApi: false,
        accept: {
            "image/*": [".jpg", ".jpeg"],
            "image/jpg": [],
            "image/jpeg": []
        },
        getFilesFromEvent: event => {
            const fromFilePicker = event._reactName === "onChange";
            const fromDragAndDrop = event._reactName === "onDrop";

            if (fromFilePicker) {
                return normalizeFilenames(event.target.files);
            }

            if (fromDragAndDrop) {
                return normalizeFilenames(event.dataTransfer.files);
            }

            return [];

            function normalizeFilenames(files = {}) {
                if (!files || files.length === 0) return [];

                const processedFiles = [];

                for (const key in files) {
                    if (key === "length" || key === "item") continue;

                    const file = files[key];

                    processedFiles.push(
                        new File([file], `${file.name.normalize("NFC")}`, {
                            type: file.type
                        })
                    );
                }

                return processedFiles;
            }
        },
        noDragEventsBubbling: true,
        onDropAccepted: acceptedFiles => {
            handleDropAccepted({
                uploadFiles,
                acceptedFiles,
                handleClearErrors,
                setMaxFiles,
                maxFiles,
                onDropAccepted
            });
        },
        onDropRejected: fileRejections => {
            handleDropRejected({
                fileRejections,
                setRejectedFileErrors
            });
        }
    });

    const isDropzoneEmpty = filesInDropzoneCount === 0;

    const rejectedFilesCount =
        hasRejectedFileErrors && hasRejectedFileErrors.length;

    useEffect(() => {
        if (isDragAccept) {
            handleClearErrors();
        }

        return () => handleClearErrors();
    }, [isDragAccept]);

    return {
        getRootProps,
        getInputProps,
        isDropzoneEmpty,
        isDragAccept,
        isDragReject,
        initialMaximumFiles,
        maxFiles,
        setMaxFiles,
        minFileSizeBytes,
        maxFileSizeBytes,
        acceptedFiles,
        isDisabledDropzone,
        setDisabledDropzone,
        handleClearErrors,
        hasRejectedFileErrors,
        rejectedFilesCount,
        addedFilesComponent,
        openFilePicker,
        filesInDropzoneCount
    };
};

export const DropzoneProvider = ({ children }) => {
    const [isDisabledDropzone, setDisabledDropzone] = useState(false);
    const [hasRejectedFileErrors, setRejectedFileErrors] = useState([]);

    return (
        <DropzoneContext.Provider
            value={{
                isDisabledDropzone,
                setDisabledDropzone,
                hasRejectedFileErrors,
                setRejectedFileErrors
            }}
        >
            {children}
        </DropzoneContext.Provider>
    );
};
